import * as React from 'react'
import * as Redux from 'react-redux'

import { useMediaQuery } from 'react-responsive'
import { styled, css } from 'next-yak'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'
import * as models from '@owl-nest/models'
import { t } from '@owl-nest/localize'
import * as money from '@owl-nest/money'
import * as plume from '@ulule/owl-kit-components/next'
import { PM_MENU_WIDTH } from '@owl-nest/project-manager-nav'
import { CATEGORY, EVENT_TYPE, useTracking } from '@owl-nest/shadow'
import { styles } from '@owl-nest/utils'
import * as env from '@owl-nest/config'

import * as projectModel from '../../../../models/project'
import { RootState } from '../../../../../reducers'
import * as URLs from '../../../../utils/URLs'
import * as modalAction from '../../../../../actions/modal'

import { CreateButton } from '../CreateButton'
import { DonationBlock } from '../DonationBlock'
import { Heading } from './Heading'
import { LiveEvents } from './LiveEvents'
import { AdviceModal } from '../AdviceModal'
import { Owner } from './Owner'
import { PartnerRibbon } from './PartnerRibbon'
import { PrimaryMedia } from './PrimaryMedia'
import { Statistics } from './Statistics'
import { SocialActions } from './SocialActions'
import { Sponsors } from './Sponsors'
import { TRACKING_ACTION, TRACKING_SOURCE } from '../../utils/trackerEnums'
import { DisplayMode } from '../../../../context'
import { BackersBlock, BlockStatus, useBlockStatus } from '../BackersBlock'
import { useLiveEventsSocket } from './hooks'
import { MIDDLE_TABLET_LAPTOP_BREAKPOINT, EDIT_MODE_SMALL_LAPTOP, EDIT_MODE_TABLET_BREAKPOINT } from '../../constants'

export function HeroHeader(): React.ReactElement<unknown> {
  const dispatch = Redux.useDispatch()
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const displayMode = React.useContext(DisplayMode)
  const isEditMode = displayMode === 'edit'
  const userConfig = env.useUserEnv()

  const { liveEventsSocketReturn } = useLiveEventsSocket()
  const tracker = useTracking()

  const isDesktopOrLaptop = useMediaQuery({
    query: isEditMode ? plume.BREAKPOINTS.LAPTOP : MIDDLE_TABLET_LAPTOP_BREAKPOINT,
  })
  const [isAdviceModalOpen, setAdviceModalOpen] = React.useState(false)
  const [isSecurePaymentModalOpen, setSecurePaymentModalOpen] = React.useState(false)

  const backgroundColor = models.project.backgroundColor<projectModel.ExtraFields>(project)
  const backgroundImage = models.project.backgroundImage<projectModel.ExtraFields>(project)
  const hasBackgroundOrImage =
    (backgroundColor !== undefined && plume.utils.colors.hexToRgb(backgroundColor, 0) !== 'rgb(255,255,255)') ||
    backgroundImage !== undefined

  const isDonationBased = models.project.isDonationBased(project)
  const isFinished = models.project.isFinished<projectModel.ExtraFields>(project)

  const setIsTaxDeductionModalOpen = hooks.useSetModal('DonationTaxDeductionInfo')
  const backersBlockStatus = useBlockStatus()

  const isUserSupporter = Number(project.user_orders?.length) > 0
  const isBackerBlockDisplayed =
    backersBlockStatus === BlockStatus.CANCELLED ||
    backersBlockStatus === BlockStatus.FINISHED_DONATION_BASED ||
    backersBlockStatus === BlockStatus.SUCCESS_ACTIVE_ONBOARDING ||
    backersBlockStatus === BlockStatus.SUCCESS_INACTIVE_ONBOARDING ||
    (isUserSupporter &&
      (backersBlockStatus === BlockStatus.FINISHED_UNSUCCESSFULLY || backersBlockStatus === BlockStatus.ONLINE))

  const isSponsorsDisplayed = Boolean(project.sponsorships)

  const isLiveEventsDisplayed = models.project.isOnline(project) && Boolean(liveEventsSocketReturn.events?.events)

  return (
    <Wrapper
      $backgroundColor={backgroundColor}
      $backgroundImage={backgroundImage}
      $isEditMode={isEditMode}
      $isSponsorsDisplayed={isSponsorsDisplayed}
      $isLiveEventsDisplayed={isLiveEventsDisplayed}
    >
      {backersBlockStatus !== BlockStatus.NONE && <BackersBlock status={backersBlockStatus} />}

      {!isDesktopOrLaptop && <PrimaryMedia />}
      {!hasBackgroundOrImage && <PartnerRibbon />}

      <Content $backerBlockIsActive={isBackerBlockDisplayed} $isEditMode={isEditMode}>
        <Heading>{hasBackgroundOrImage && <PartnerRibbon />}</Heading>

        <Information $isEditMode={isEditMode}>
          <InformationTorso $isEditMode={isEditMode}>
            {isDesktopOrLaptop && <PrimaryMedia />}
            {isEditMode && project.goal === 0 && !isDonationBased ? (
              <ActionsAndStatisticsEdit $isEditMode={isEditMode} $withCTA={!isFinished}>
                <CreateButton
                  center={true}
                  createHandler={() => {
                    document.location.href = `/projects/${project.id}/edit/?goal`
                  }}
                  cta={
                    <plume.styles.copy.S.regular>
                      <plume.Link
                        onClick={() => {
                          if (userConfig.lang === 'fr') {
                            setAdviceModalOpen(true)
                          } else {
                            document.location.href = getAdviceURL(userConfig.lang)
                          }
                        }}
                        tinted
                        withIcon
                      >
                        <plume.glyphs.stroke.Bulb size={15} />
                        {t('How to set your goal')}
                      </plume.Link>
                    </plume.styles.copy.S.regular>
                  }
                  title={
                    <CreateButtonTitle>
                      <plume.styles.heading.M>{t('Set your goal')}</plume.styles.heading.M>
                      <plume.styles.heading.L>{t('Set your goal')}</plume.styles.heading.L>
                    </CreateButtonTitle>
                  }
                />
              </ActionsAndStatisticsEdit>
            ) : (
              <ActionsAndStatistics $isEditMode={isEditMode} $withCTA={!isFinished}>
                <Statistics />

                <CenteredOnMobile $pullContent={isFinished && !project.goal_raised} $isEditMode={isEditMode}>
                  {!isFinished ? (
                    isDonationBased ? (
                      <DonationBlock context="project-header" project={project} withFromMention />
                    ) : (
                      <>
                        <ContributionCTAWrapper>
                          <plume.styles.button.CallToAction
                            disabled={!models.project.isOnline(project)}
                            onClick={goToCheckout}
                            size="medium"
                          >
                            <span>{t('Back this project')}</span>
                            {project.user_role === api.UserRole.Supporter ? (
                              <small>{t('How generous!')}</small>
                            ) : (
                              !isDonationBased && (
                                <small>
                                  {t('Starting at ')}
                                  <money.MoneyDisplayer
                                    amount={project.lowest_contribution_amount || 0}
                                    project={project}
                                  />
                                </small>
                              )
                            )}
                          </plume.styles.button.CallToAction>
                          {!isDonationBased && project.tax_deductible && (
                            <TaxInfo
                              tinted
                              type="button"
                              withIcon
                              onClick={() => {
                                setIsTaxDeductionModalOpen(true)
                              }}
                            >
                              <plume.glyphs.stroke.Percent size={18} />
                              {t('This donation is tax-deductible!')}
                            </TaxInfo>
                          )}
                          {!isDonationBased && (
                            <plume.LinkAsButton
                              kind="secondary"
                              onClick={() => {
                                setSecurePaymentModalOpen(true)
                              }}
                              underlined
                            >
                              {t('Secure and flexible payment')}
                            </plume.LinkAsButton>
                          )}
                        </ContributionCTAWrapper>
                      </>
                    )
                  ) : isDonationBased ? (
                    <InvitationToFollowEndedProject>
                      {project.goal_status === api.GoalStatus.SUCCESS
                        ? t('This fundraiser is now over. To stay up to date, follow it here:')
                        : project.goal_status === api.GoalStatus.FAILED
                          ? t('This fundraiser has not been funded. To stay up to date, follow it here:')
                          : t('This fundraiser was cancelled. To stay up to date, follow it here:')}
                      <SocialActions />
                    </InvitationToFollowEndedProject>
                  ) : !project.goal_raised ? (
                    <InvitationToFollowEndedProject>
                      {t('This project has not been funded. To stay up to date, follow it here:')}
                      <SocialActions />
                    </InvitationToFollowEndedProject>
                  ) : (
                    <InvitationToDiscoverOtherProjects>
                      {t('Missed the campaign? Discover more %(mainTag)s live projects 👇', {
                        mainTag: models.project.mainTag<projectModel.ExtraFields>(project),
                      })}
                      <plume.styles.button.CallToAction kind="secondary" onClick={goToMainCategory} size="small">
                        {t('All online projects')}
                      </plume.styles.button.CallToAction>
                    </InvitationToDiscoverOtherProjects>
                  )}
                </CenteredOnMobile>
              </ActionsAndStatistics>
            )}
          </InformationTorso>

          <InformationFooter $isEditMode={isEditMode}>
            <Owner />
            {(!isFinished || (project.goal_raised && !isDonationBased)) && <SocialActions />}
          </InformationFooter>
        </Information>
      </Content>
      <SponsorsAndEventsContainer $isEditMode={isEditMode}>
        {isSponsorsDisplayed && <Sponsors />}
        {isLiveEventsDisplayed && <LiveEvents socket={liveEventsSocketReturn} />}
      </SponsorsAndEventsContainer>

      <AdviceModal
        link={`/projects/${project.id}/edit/`}
        open={isAdviceModalOpen}
        setOpen={setAdviceModalOpen}
        title={t('How to set your goal')}
        videoHtml='<iframe allow="accelerometer; autoplay; encrypted-media;" allowFullScreen frameBorder="0" src="https://www.youtube.com/embed/WowF92vN55w?autoplay=1" title="video-placeholder" />'
      />

      <money.SecurePaymentModal
        open={isSecurePaymentModalOpen}
        projectIsDonationBased={isDonationBased}
        setOpen={setSecurePaymentModalOpen}
      />
    </Wrapper>
  )

  function goToMainCategory(): void {
    document.location.href = project.main_tag?.absolute_url ?? ''
  }

  function goToCheckout(): void {
    if (!models.project.isOrdersEnabled<projectModel.ExtraFields>(project)) {
      dispatch(modalAction.toggleModalPaymentDisturbance(true))
    } else {
      tracker.track({
        event: EVENT_TYPE.GENERIC,
        eventCategory: CATEGORY.PROJECT_PAGE,
        eventAction: TRACKING_ACTION.ACCESS_CHECKOUT,
        eventLabel: TRACKING_SOURCE.HERO,
        eventCallback: () => {
          document.location.href = URLs.getCheckoutURL(project.id)
        },
      })
    }
  }
}

function getAdviceURL(lang: api.Lang): string {
  let url
  switch (lang) {
    case 'en':
      url = 'https://support.ulule.com/hc/en-us/articles/360001845637-How-to-define-your-goal-'
      break

    case 'es':
      url =
        'https://community.ulule.com/topics/dividir-una-campana-en-tramos-recauda-mas-con-un-objetivo-menor-stretchgoals-14191/'
      break

    case 'it':
      url =
        'https://community.ulule.com/topics/le-campagne-di-crowdfunding-e-il-loro-obiettivo-finanziario-come-calcolarlo-e-quando-perche-applicare-la-strategia-dello-step-by-step-13945/'
      break

    case 'nl':
      url = 'https://support.ulule.com/hc/nl/articles/360001845637-Hoe-je-doel-bepalen'
      break

    default:
      url = 'https://support.ulule.com/hc/en-us/articles/360001845637-How-to-define-your-goal-'
      break
  }

  return url
}

const actionsAndStatisticsBreakpointStyle1 = css<{ $withCTA: boolean; $isEditMode: boolean }>`
  display: flex;
  flex-direction: column;
  margin: 0;
  max-width: 420px;
  min-width: 340px;
  width: ${({ $isEditMode }) => ($isEditMode ? '38%' : '35%')};

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        right: 0;
      `
    }
  }}

  ${({ $isEditMode, $withCTA }) => {
    if ($withCTA && !$isEditMode) {
      return css`
        right: 0;
      `
    }
  }}
`

const ActionsAndStatistics = styled.div<{ $withCTA: boolean; $isEditMode: boolean }>`
  margin: 0 15px;

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          ${actionsAndStatisticsBreakpointStyle1};
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${actionsAndStatisticsBreakpointStyle1};
        }
      `
    }
  }}

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${EDIT_MODE_SMALL_LAPTOP} {
          min-width: 356px;
          width: 45%;
        }
      `
    }
  }}


  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    margin: 0;
  }
`

const actionsAndStatisticsEditBreakpointStyle = css`
  ${plume.styles.heading.M} {
    display: block;
  }

  ${plume.styles.heading.L} {
    display: none;
  }
`

const ActionsAndStatisticsEdit = styled(ActionsAndStatistics)`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${plume.styles.heading.M} {
    display: none;
  }

  ${plume.styles.heading.L} {
    display: block;
  }

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          ${actionsAndStatisticsEditBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${actionsAndStatisticsEditBreakpointStyle};
        }
      `
    }
  }}
`

const centeredOnMobileBreakpointStyle = css`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: 500px;
`

const CenteredOnMobile = styled.div<{ $pullContent?: boolean; $isEditMode: boolean }>`
  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
          ${centeredOnMobileBreakpointStyle};
        }
      `
    } else {
      return css`
        @media not screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${centeredOnMobileBreakpointStyle};
        }
      `
    }
  }}

  ${({ $pullContent = false, $isEditMode }) => {
    if ($pullContent)
      return css`
        @media screen and (max-width: 1250px) {
          margin-bottom: -46px;
        }

        @media not screen and ${$isEditMode ? plume.BREAKPOINTS.LAPTOP : MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          margin-bottom: 0;
        }
      `
  }}
`

const contentBreakpointStyle = css<{ $backerBlockIsActive: boolean }>`
  margin: 0 auto;
  max-width: 1200px;
  position: relative;
  top: 26px;
  margin-top: 44px;
  width: 100%;
  z-index: 1;

  ${({ $backerBlockIsActive }) => {
    if ($backerBlockIsActive) {
      return css`
        margin-top: 0;
      `
    }
  }}
`

const Content = styled.div<{ $backerBlockIsActive: boolean; $isEditMode: boolean }>`
  border-radius: 4px;
  box-shadow: ${plume.EFFECTS.SHADOW_4};
  background-color: ${plume.COLORS.PRIMARY_GREY_000};

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
          ${contentBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${contentBreakpointStyle};
        }
      `
    }
  }}
`

const ContributionCTAWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${plume.styles.button.CallToAction} {
    display: inline-flex;
    flex-direction: column;
    height: 80px;
    line-height: 18px;
  }

  small {
    font-size: ${() => styles.rem('15px')};
    font-weight: normal;
    margin-top: 4px;
    text-transform: none;
  }
`

const Information = styled.div<{ $isEditMode: boolean }>`
  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          margin: 0px ${({ $isEditMode }) => ($isEditMode ? '20px' : '40px')};
          padding: 0;
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          margin: 0px ${({ $isEditMode }) => ($isEditMode ? '20px' : '40px')};
          padding: 0;
        }
      `
    }
  }}

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    margin: 0 40px;
  }
`

const InformationFooter = styled.div<{ $isEditMode: boolean }>`
  display: flex;
  flex-direction: column-reverse;
  margin-left: 15px;
  margin-right: 15px;
  padding-top: 15px;
  padding-bottom: 25px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    max-width: 100%;
    justify-content: space-between;
    flex-direction: row;
  }

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          margin-left: 0px;
          margin-right: 0px;
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          margin-left: 0px;
          margin-right: 0px;
        }
      `
    }
  }}
`

const infromationTorsoBreakpointStyle = css`
  align-items: center;
  display: flex;
  justify-content: space-between;
  max-height: 380px;
  max-width: 1200px;
`

const InformationTorso = styled.div<{ $isEditMode: boolean }>`
  position: relative;
  max-height: none;

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          ${infromationTorsoBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${infromationTorsoBreakpointStyle};
        }
      `
    }
  }}

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    max-height: 380px;
    max-width: 100%;
  }
`

const InvitationToFollowEndedProject = styled(plume.styles.heading.XXXS)`
  border: 1px solid ${plume.COLORS.PRIMARY_SAND_200};
  border-radius: 4px;
  padding: 26px 26px 18px;

  div {
    margin-top: 10px;
  }
`

const InvitationToDiscoverOtherProjects = styled(plume.styles.copy.S.regular)`
  ${plume.styles.button.CallToAction} {
    margin-top: 20px;
  }
`

type WrapperProps = {
  $backgroundColor?: string
  $backgroundImage?: string
  $isEditMode: boolean
  $isSponsorsDisplayed: boolean
  $isLiveEventsDisplayed: boolean
}

const wrapperBreakpointStyle = css<WrapperProps>`
  padding-bottom: 45px;
  padding-top: 20px;

  &::after {
    content: '';
    ${({ $backgroundColor }) => {
      if ($backgroundColor) {
        return css`
          background-color: ${() => $backgroundColor};
        `
      }
    }}
    ${({ $backgroundImage }) => {
      if ($backgroundImage) {
        return css`
          background-image: ${() => `url('${$backgroundImage}')`};
        `
      }
    }}
      background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    ${({ $isSponsorsDisplayed, $isLiveEventsDisplayed }) => {
      let distance = 130

      if ($isSponsorsDisplayed) {
        distance += 60
      } else if ($isLiveEventsDisplayed) {
        distance += 53
      }

      return css`
        height: calc(100% - ${() => distance}px);
      `
    }}
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;
  }
`

const Wrapper = styled.div<WrapperProps>`
  position: relative;

  ${({ $isEditMode }) =>
    $isEditMode &&
    css`
      @media screen and ${EDIT_MODE_TABLET_BREAKPOINT} {
        border-left: 1px solid ${plume.COLORS.PRIMARY_SAND_100};
        margin-left: ${PM_MENU_WIDTH};
        z-index: 2;
      }
    `};

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${EDIT_MODE_TABLET_BREAKPOINT} {
          ${wrapperBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${wrapperBreakpointStyle};
        }
      `
    }
  }}
`

const CreateButtonTitle = styled.span`
  margin-bottom: 4px;
`

const sponsorsAndEventsContainerBreakpointStyle = css`
  border-radius: 0px 0px 4px 4px;
  box-shadow: ${plume.EFFECTS.SHADOW_4};
  flex-direction: row;
  top: 23px;
  padding-left: 0px;
  padding-right: 0px;
  margin: 0 auto;

  & > div {
    margin-left: 40px;
    margin-right: 40px;
  }
`

const SponsorsAndEventsContainer = styled.div<{ $isEditMode: boolean }>`
  position: relative;
  top: -15px;
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  display: flex;
  justify-content: space-between;
  padding: 15px;
  flex-direction: column;
  max-width: 1200px;

  &:empty {
    display: none;
  }

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
          & > div + div {
            margin-top: 20px;
          }
        }
      `
    } else {
      return css`
        @media not screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          & > div + div {
            margin-top: 20px;
          }
        }
      `
    }
  }}

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    align-items: center;

    & > div {
      &:only-child {
        margin: 0 auto;
      }
    }
  }

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
          ${sponsorsAndEventsContainerBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${sponsorsAndEventsContainerBreakpointStyle};
        }
      `
    }
  }}
`

const TaxInfo = styled(plume.LinkAsButton)`
  margin: 12px auto 0 auto;
  text-align: center;
  width: fit-content;
  padding: 0px;
  flex: 0 0 100%;

  ${plume.glyphs.stroke.Percent} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
    margin-right: 6px;
    margin-top: -1px;
  }
`
